import type { Preview } from '../rooms/types';
import type { Place } from './location';
import type { ForwardOrigin } from './forward';

/**
 * Сообщение — так, как его видит лента. Свой тип, а не `MatrixEvent` (см. план,
 * «Архитектура»); строится `TimelineMapper` и только им.
 */

/**
 * Ключ зашифрованного вложения — `EncryptedFile` из спецификации без `url`: JWK, IV и хэш
 * шифротекста. Живёт в памяти ровно столько, сколько событие; на диск его пишет SDK, не мы.
 */
export interface EncryptedFileKey {
  key: { kty: string; key_ops: string[]; alg: string; k: string; ext: boolean };
  iv: string;
  hashes: Record<string, string>;
  v: string;
}

/** Где лежат байты: `mxc://`, и если вложение зашифровано — ключ к нему. */
export interface MediaSource {
  mxc: string;
  encryption?: EncryptedFileKey;
}

export interface Attachment {
  /** Имя файла: `filename`, а если его нет — `body`. */
  name: string;
  /** Подпись (Matrix 1.10): `body`, когда рядом есть отдельное `filename`. */
  caption?: string;
  mimetype?: string;
  size?: number;
  width?: number;
  height?: number;
  /** мс */
  duration?: number;
  /** Нет — вложение без байтов (битое событие): показать можно только подпись. */
  source?: MediaSource;
  thumbnail?: { source: MediaSource; width?: number; height?: number; mimetype?: string };
  /** Голосовое: волна отправителя, 0…1 (`org.matrix.msc1767.audio`). Пусто — не прислали. */
  waveform?: number[];
}

/**
 * Служебная строка. Про людей — список, а не одно имя: подряд идущие «вошла Аня», «вошёл
 * Борис» склеиваются в одну строку (`layout.ts`), как в нативной Искре.
 */
export type ServiceEvent =
  | { type: 'joined' | 'left' | 'invited' | 'removed' | 'banned'; people: string[] }
  | { type: 'renamedThemselves'; from: string; to: string }
  | { type: 'roomRenamed'; name: string }
  | { type: 'roomTopicChanged' }
  | { type: 'roomPictureChanged' }
  | { type: 'roomCreated' }
  | { type: 'encryptionEnabled' }
  /** Событие, которого лента не показывает, — только при «Все события в чате»: тип, деталь и отправитель. */
  | { type: 'hidden'; description: string };

export interface PollAnswer {
  id: string;
  text: string;
  votes: number;
}

export interface Poll {
  question: string;
  answers: PollAnswer[];
  /** Сколько человек проголосовало (не голосов: при нескольких ответах их больше). */
  voters: number;
  /** Мой выбор — ID ответов; пусто — не голосовал. */
  mine: string[];
  maxSelections: number;
  /** Итоги скрыты до конца опроса (`undisclosed`). */
  undisclosed: boolean;
  ended: boolean;
  /** Пространство имён опроса — ответ уходит в том же, в каком опрос пришёл. */
  stable: boolean;
}

export type MessageKind =
  /** `html` — `formatted_body`, как пришёл: чистит его экран (DOMPurify), не ядро. */
  | { type: 'text'; body: string; html?: string }
  | { type: 'emote'; body: string; html?: string }
  | { type: 'notice'; body: string; html?: string }
  | { type: 'image' | 'sticker' | 'video' | 'videoNote' | 'voice' | 'audio' | 'file'; attachment: Attachment }
  /** Место — уже разобранное и проверенное: широта 900 сюда не доходит (`location.ts`). */
  | { type: 'location'; body: string; uri: string; place: Place }
  | { type: 'poll'; poll: Poll }
  | { type: 'service'; event: ServiceEvent }
  /** `at` — когда удалили, если сервер сказал (`redacted_because`). */
  | { type: 'deleted'; at?: number }
  | { type: 'unreadable' }
  | { type: 'unsupported' };

/** Почему не ушло — как `SendFailure` нативной Искры. */
export type SendFailure = 'insecureDevices' | 'identityChanged' | 'verificationRequired' | 'server';

export type Delivery = { state: 'sending' } | { state: 'sent' } | { state: 'failed'; reason: SendFailure };

export interface Reaction {
  key: string;
  count: number;
  mine: boolean;
}

export interface ReplyPreview {
  eventId: string;
  senderName?: string;
  /** Текст оригинала одной строкой — или пусто, если его не видно. */
  text?: string;
}

/** Строка под сообщением, на которое ответили в треде: «3 ответа» и последний из них. */
export interface ThreadSummary {
  replies: number;
  latestSender?: string;
  /** Последний ответ — как в превью списка: слова или вид вложения. */
  latest?: Preview;
}

export interface Message {
  /** Стабильный ключ строки: не меняется, когда local echo получает настоящий ID. */
  key: string;
  eventId?: string;
  kind: MessageKind;
  senderId: string;
  senderName: string;
  senderAvatarUrl?: string;
  own: boolean;
  ts: number;
  delivery: Delivery;
  edited: boolean;
  replyTo?: ReplyPreview;
  /** Пересланное — `m.forwarded` в содержимом (см. `forward.ts`). */
  forwarded?: ForwardOrigin;
  reactions: Reaction[];
  pinned: boolean;
  /** Тред под этим сообщением — когда в нём уже отвечали. */
  thread?: ThreadSummary;
  /** Это ответ в треде — ID корня. */
  threadRootId?: string;
  /** Своё, ушедшее — и кто-то из собеседников уже прочитал (по квитанциям чтения). */
  read?: boolean;
  /**
   * «Глаз» — только у самого нового своего прочитанного (`carriesReadMark` нативной Искры):
   * старое прочитанное ничего не рисует, взгляд ниже уже сказал за него.
   */
  readMark?: boolean;
  /** Своё текстовое, ушедшее, не удалённое. */
  canEdit: boolean;
  /** Своё — или право модератора удалять чужое. */
  canDelete: boolean;
  /**
   * Альбом (`album.ts`): эта строка — первое из фото и видео, отправленных вместе, а здесь все
   * они по порядку, включая её саму. Нет — обычное сообщение.
   */
  album?: Message[];
}

export const isService = (m: Message): boolean => m.kind.type === 'service';
